import React from 'react';
import {
  Atom,
  Plugins,
  withMeta,
  withSystem,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  typeName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';
import {TTypeConstructor, TListOfElement} from '@packages/systems/wfdl-ast';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';

const pageBuildingPlugin = Plugins.name('PageBuilding');

/**
 * Exported separately from `Atom.createRender` so tests can render it as a
 * regular React component.
 *
 * Unconnected, it exists only for the Designer: published and exported output
 * gets nothing. Once a form is connected it is a plain container, rendered the
 * same everywhere so the published layout matches the canvas.
 */
export const FormPlaceholderRender = ({
  context,
  data,
  attributes,
  classes,
  generateAttributes,
  renderChild,
}: AtomRenderProps) => {
  const runtimeEnvironment = useRuntimeEnvironment();
  const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
  const children = getChildren(data);

  const isPublishedOutput =
    runtimeEnvironment === WebflowEnvironments.SSR ||
    runtimeEnvironment === WebflowEnvironments.EXPORT;
  if (isPublishedOutput && children.length === 0) return null;

  return (
    <Tag
      {...generateAttributes(attributes, classes.push('w-form-placeholder'))}
    >
      {children.map((child: EvaluatedNodeData) => renderChild(child, context))}
    </Tag>
  );
};

const FormPlaceholder = createIntegratedAtom({
  showEmpty: true,
  icon: {
    type: 'Component',
    value: [pageBuildingPlugin, iconName('FormPlaceholderIcon')],
  },
  displayName: 'Form Placeholder',
  render: Atom.createRender(FormPlaceholderRender),
  fields: {
    children: withSystem({private: true}, TListOfElement),
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
      )
    ),
  },
});

export default FormPlaceholder;
